import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { usePermissions } from '@/lib/roles';
import { Badge } from '@/components/ui/badge';
import { Calendar, ChevronLeft, ChevronRight, Bell, Check, X, AlertTriangle, Clock, Package, Filter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CustomSelect } from '@/components/ui/custom-select';

const statusColors = {
  planned: 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600',
  in_progress: 'bg-[#f0dcf0] dark:bg-[#98139C]/30 border-[#98139C]',
  completed: 'bg-green-100 dark:bg-green-900/30 border-green-500',
  blocked: 'bg-red-100 dark:bg-red-900/30 border-red-500',
};

const urgencyColors = {
  P0: 'bg-red-500',
  P1: 'bg-amber-500',
  P2: 'bg-blue-500',
};

export default function Roadmap() {
  const can = usePermissions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const highlightRef = useRef(null);
  
  const [roadmap, setRoadmap] = useState({ months: [], products: [] });
  const [pendingChanges, setPendingChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthOffset, setMonthOffset] = useState(0);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [processingChange, setProcessingChange] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [highlightedFeature, setHighlightedFeature] = useState(null);

  const visibleMonths = 6;

  useEffect(() => {
    loadRoadmap();
    loadPendingChanges();
  }, []);

  // Handle highlight from URL parameter
  useEffect(() => {
    if (highlightId && !loading) {
      setHighlightedFeature(highlightId);
      
      // Scroll to the highlighted element after a short delay
      setTimeout(() => {
        if (highlightRef.current) {
          highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
      
      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedFeature(null);
        // Clear the URL parameter
        navigate('/roadmap', { replace: true });
      }, 3000);
    }
  }, [highlightId, loading, navigate]);

  const loadRoadmap = async () => {
    try {
      const res = await api.get('/roadmap/', { params: { months: 12 } });
      setRoadmap(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadPendingChanges = async () => {
    try {
      const res = await api.get('/roadmap/changes');
      setPendingChanges(res.data);
    } catch (err) { console.error(err); }
  };

  // Filter products based on selection
  const filteredProducts = selectedProduct === 'all' 
    ? roadmap.products 
    : roadmap.products.filter(p => p.id === selectedProduct);
  
  // Calculate totals for filtered view
  const totalFeatures = filteredProducts.reduce((sum, p) => sum + p.features.length, 0);

  const approveChange = async (changeId) => {
    setProcessingChange(changeId);
    try {
      await api.patch(`/roadmap/changes/${changeId}/approve`);
      setPendingChanges(pendingChanges.filter(c => c.id !== changeId));
      loadRoadmap();
    } catch (err) { console.error(err); }
    finally { setProcessingChange(null); }
  };

  const rejectChange = async (changeId) => {
    setProcessingChange(changeId);
    try {
      await api.patch(`/roadmap/changes/${changeId}/reject`);
      setPendingChanges(pendingChanges.filter(c => c.id !== changeId));
    } catch (err) { console.error(err); }
    finally { setProcessingChange(null); }
  };

  // Get visible months based on offset
  const displayMonths = roadmap.months.slice(monthOffset, monthOffset + visibleMonths);

  // Format month for display
  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Calculate feature bar position and width
  const getFeaturePosition = (feature) => {
    const startIdx = displayMonths.indexOf(feature.start_month);
    const endIdx = displayMonths.indexOf(feature.end_month);
    
    // Check if feature is visible in current view
    const featureStart = roadmap.months.indexOf(feature.start_month);
    const featureEnd = roadmap.months.indexOf(feature.end_month);
    const viewStart = monthOffset;
    const viewEnd = monthOffset + visibleMonths - 1;
    
    // Not visible
    if (featureEnd < viewStart || featureStart > viewEnd) {
      return null;
    }
    
    // Calculate visible portion
    const visibleStart = Math.max(0, startIdx);
    const visibleEnd = endIdx >= 0 ? endIdx : visibleMonths - 1;
    
    // Extends before view
    const extendsBefore = featureStart < viewStart;
    // Extends after view
    const extendsAfter = featureEnd > viewEnd;
    
    const left = extendsBefore ? 0 : (visibleStart / visibleMonths) * 100;
    const width = ((visibleEnd - (extendsBefore ? -1 : visibleStart) + 1) / visibleMonths) * 100;
    
    return { left: `${left}%`, width: `${Math.min(width, 100 - left)}%`, extendsBefore, extendsAfter };
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="roadmap-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Strategic</p>
          <h1 className="text-4xl font-heading text-[#30302F] dark:text-white mt-1" style={{ letterSpacing: '-0.8px' }}>Roadmap</h1>
          <p className="text-[#30302F] dark:text-white mt-2 font-light text-lg">
            {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} • {totalFeatures} feature{totalFeatures !== 1 ? 's' : ''}
            {selectedProduct !== 'all' && <span className="text-[#98139C] ml-2">(filtered)</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Product Filter */}
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-[#30302F] dark:text-white" />
            <CustomSelect
              data-testid="product-filter"
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              options={[
                { value: 'all', label: 'All Products' },
                ...roadmap.products.map(p => ({ value: p.id, label: `${p.name} (${p.features.length})` }))
              ]}
              aria-label="Filter roadmap by product"
            />
          </div>
          
          {/* Pending Changes Notification */}
          {pendingChanges.length > 0 && (
            <button
              onClick={() => setShowChangesModal(true)}
              className="relative bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-4 h-10 flex items-center gap-2 text-sm font-medium hover:bg-amber-200 dark:hover:bg-amber-900/30 transition-colors"
              data-testid="pending-changes-btn"
            >
              <Bell size={16} />
              {pendingChanges.length} Pending Changes
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center">
                {pendingChanges.length}
              </span>
            </button>
          )}
          
          {/* Month Navigation */}
          <div className="flex items-center gap-1 border border-[#e3e3e3] dark:border-white/10">
            <button
              onClick={() => setMonthOffset(Math.max(0, monthOffset - 1))}
              disabled={monthOffset === 0}
              className="w-9 h-9 flex items-center justify-center text-[#30302F] dark:text-white hover:bg-[#faf8fb] dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="prev-month-btn"
              aria-label="Show previous months"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="px-3 text-sm text-[#30302F] dark:text-white font-medium">
              {displayMonths.length > 0 && `${formatMonth(displayMonths[0])} - ${formatMonth(displayMonths[displayMonths.length - 1])}`}
            </span>
            <button
              onClick={() => setMonthOffset(Math.min(roadmap.months.length - visibleMonths, monthOffset + 1))}
              disabled={monthOffset >= roadmap.months.length - visibleMonths}
              className="w-9 h-9 flex items-center justify-center text-[#30302F] dark:text-white hover:bg-[#faf8fb] dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="next-month-btn"
              aria-label="Show next months"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Timeline Header */}
      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 mb-0">
        <div className="flex">
          <div className="w-64 shrink-0 p-4 border-r border-[#e3e3e3] dark:border-white/10">
            <span className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Products & Features</span>
          </div>
          <div className="flex-1 flex">
            {displayMonths.map((month, idx) => {
              const isCurrentMonth = month === new Date().toISOString().slice(0, 7);
              return (
                <div 
                  key={month} 
                  className={`flex-1 p-4 text-center border-r border-[#e3e3e3] dark:border-white/10 last:border-r-0 ${isCurrentMonth ? 'bg-[#f0dcf0]/30 dark:bg-[#98139C]/10' : ''}`}
                >
                  <span className={`text-xs font-bold ${isCurrentMonth ? 'text-[#98139C]' : 'text-[#30302F] dark:text-white'}`}>
                    {formatMonth(month)}
                  </span>
                  {isCurrentMonth && (
                    <div className="text-[10px] text-[#98139C] mt-0.5">Current</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Products and Features */}
      {filteredProducts.length === 0 ? (
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 border-t-0 py-16 text-center">
          <Package size={40} className="mx-auto mb-3 text-[#30302F] dark:text-white" />
          <p className="text-[#30302F] dark:text-white text-lg font-heading">
            {roadmap.products.length === 0 ? 'No products yet' : 'No matching products'}
          </p>
          <p className="text-[#30302F] dark:text-white text-sm mt-1 font-light">
            {roadmap.products.length === 0 
              ? 'Create products and link features to see them on the roadmap'
              : 'Try selecting a different product filter'}
          </p>
        </div>
      ) : (
        <div className="border border-[#e3e3e3] dark:border-white/10 border-t-0">
          {filteredProducts.map((product, pIdx) => (
            <div key={product.id} className={`${pIdx > 0 ? 'border-t border-[#e3e3e3] dark:border-white/10' : ''}`}>
              {/* Product Row */}
              <div className="flex bg-[#faf8fb] dark:bg-white/[0.02]">
                <div 
                  className="w-64 shrink-0 p-4 border-r border-[#e3e3e3] dark:border-white/10 cursor-pointer hover:bg-[#f0dcf0]/30 dark:hover:bg-[#98139C]/10 transition-colors"
                  onClick={() => navigate(`/products/${product.id}`)}
                  data-testid={`product-row-${product.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Package size={14} className="text-[#98139C]" />
                    <span className="text-sm font-bold text-[#30302F] dark:text-white">{product.name}</span>
                  </div>
                  <p className="text-[11px] text-[#30302F] dark:text-white mt-1">
                    {product.features.length} feature{product.features.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex-1 relative min-h-[50px]">
                  {/* Grid lines */}
                  <div className="absolute inset-0 flex">
                    {displayMonths.map((month, idx) => (
                      <div 
                        key={month} 
                        className={`flex-1 border-r border-[#e3e3e3] dark:border-white/10 last:border-r-0 ${month === new Date().toISOString().slice(0, 7) ? 'bg-[#f0dcf0]/20 dark:bg-[#98139C]/5' : ''}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Feature Rows */}
              {product.features.map((feature, fIdx) => {
                const position = getFeaturePosition(feature);
                return (
                  <div key={feature.id} className="flex bg-white dark:bg-white/5 border-t border-[#e3e3e3]/50 dark:border-white/5">
                    <div 
                      className="w-64 shrink-0 p-3 pl-8 border-r border-[#e3e3e3] dark:border-white/10 cursor-pointer hover:bg-[#faf8fb] dark:hover:bg-white/[0.02] transition-colors"
                      onClick={() => navigate(`/backlog/${feature.id}`)}
                      data-testid={`feature-row-${feature.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${urgencyColors[feature.urgency] || 'bg-gray-400'}`} />
                        <span className="text-xs text-[#30302F] dark:text-white line-clamp-1">{feature.title}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-[#30302F] dark:text-white">{feature.story_count} stories</span>
                        {feature.total_points > 0 && (
                          <span className="text-[10px] text-[#30302F] dark:text-white">• {feature.completed_points}/{feature.total_points} pts</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 relative h-14">
                      {/* Grid lines */}
                      <div className="absolute inset-0 flex">
                        {displayMonths.map((month) => (
                          <div 
                            key={month} 
                            className={`flex-1 border-r border-[#e3e3e3] dark:border-white/10 last:border-r-0 ${month === new Date().toISOString().slice(0, 7) ? 'bg-[#f0dcf0]/20 dark:bg-[#98139C]/5' : ''}`}
                          />
                        ))}
                      </div>
                      
                      {/* Feature Bar */}
                      {position && (
                        <div
                          ref={highlightedFeature === feature.id ? highlightRef : null}
                          className={`absolute top-2 bottom-2 ${statusColors[feature.status]} border-l-4 flex items-center px-2 overflow-hidden transition-all cursor-pointer hover:brightness-95 hover:shadow-md ${position.extendsBefore ? 'rounded-l-none' : 'rounded-l'} ${position.extendsAfter ? 'rounded-r-none' : 'rounded-r'} ${highlightedFeature === feature.id ? 'ring-4 ring-[#98139C] ring-opacity-75 animate-pulse shadow-lg z-10' : ''}`}
                          style={{ left: position.left, width: position.width }}
                          title={`${feature.title} (${formatMonth(feature.start_month)} - ${formatMonth(feature.end_month)}) — Click to view details`}
                          data-testid={`feature-bar-${feature.id}`}
                          onClick={() => navigate(`/backlog/${feature.id}`)}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {position.extendsBefore && <ChevronLeft size={12} className="shrink-0 opacity-50" />}
                            <span className="text-[11px] font-medium text-[#30302F] dark:text-white truncate">
                              {feature.title}
                            </span>
                            {feature.progress_percent > 0 && feature.progress_percent < 100 && (
                              <span className="text-[10px] bg-white/50 dark:bg-black/20 px-1 rounded shrink-0">
                                {feature.progress_percent}%
                              </span>
                            )}
                            {feature.status === 'completed' && <Check size={12} className="text-green-600 shrink-0" />}
                            {feature.status === 'blocked' && <AlertTriangle size={12} className="text-red-600 shrink-0" />}
                            {position.extendsAfter && <ChevronRight size={12} className="shrink-0 opacity-50" />}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Empty state for products with no features */}
              {product.features.length === 0 && (
                <div className="flex bg-white dark:bg-white/5 border-t border-[#e3e3e3]/50 dark:border-white/5">
                  <div className="w-64 shrink-0 p-3 pl-8 border-r border-[#e3e3e3] dark:border-white/10">
                    <span className="text-xs text-[#30302F] dark:text-white italic">No features linked</span>
                  </div>
                  <div className="flex-1 relative h-10">
                    <div className="absolute inset-0 flex">
                      {displayMonths.map((month) => (
                        <div key={month} className="flex-1 border-r border-[#e3e3e3] dark:border-white/10 last:border-r-0" />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-6 mt-6 text-xs text-[#30302F] dark:text-white">
        <div className="flex items-center gap-2">
          <div className={`w-4 h-3 rounded ${statusColors.planned} border-l-2`} />
          <span>Planned</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-4 h-3 rounded ${statusColors.in_progress} border-l-2`} />
          <span>In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-4 h-3 rounded ${statusColors.completed} border-l-2`} />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-4 h-3 rounded ${statusColors.blocked} border-l-2`} />
          <span>Blocked</span>
        </div>
        <div className="ml-4 flex items-center gap-4">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" /> <span>P0</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-amber-500" /> <span>P1</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-blue-500" /> <span>P2</span>
          </div>
        </div>
      </div>

      {/* Pending Changes Modal */}
      <Dialog open={showChangesModal} onOpenChange={setShowChangesModal}>
        <DialogContent className="max-w-lg rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white flex items-center gap-2">
              <Bell size={20} className="text-amber-500" />
              Pending Roadmap Changes
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {pendingChanges.length === 0 ? (
              <div className="text-center py-8 text-[#30302F] dark:text-white">
                <Check size={32} className="mx-auto mb-2 text-green-500" />
                <p>No pending changes</p>
              </div>
            ) : (
              pendingChanges.map(change => (
                <div key={change.id} className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#30302F] dark:text-white">{change.item_title}</p>
                      <p className="text-[11px] text-[#98139C] mt-0.5">{change.product_name}</p>
                    </div>
                    <Badge className={`text-[11px] rounded-none ${
                      change.change_type === 'reschedule' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' :
                      change.change_type === 'delay' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' :
                      'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {change.change_type}
                    </Badge>
                  </div>
                  
                  <p className="text-xs text-[#30302F] dark:text-white mt-2">{change.reason}</p>
                  
                  {change.change_type === 'reschedule' && (
                    <div className="flex items-center gap-2 mt-3 text-xs">
                      <span className="text-[#30302F] dark:text-white">
                        {change.old_start ? formatMonth(change.old_start) : '—'} → {change.new_start ? formatMonth(change.new_start) : '—'}
                      </span>
                      <span className="text-[#30302F] dark:text-white/10">to</span>
                      <span className="text-[#30302F] dark:text-white">
                        {change.old_end ? formatMonth(change.old_end) : '—'} → {change.new_end ? formatMonth(change.new_end) : '—'}
                      </span>
                    </div>
                  )}
                  
                  {can('decideRoadmapChange') && (
                  <div className="flex items-center gap-2 mt-4">
                    <button
                      onClick={() => approveChange(change.id)}
                      disabled={processingChange === change.id}
                      className="flex-1 h-8 bg-green-600 hover:bg-green-700 text-white text-xs font-bold flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                      data-testid={`approve-change-${change.id}`}
                    >
                      {processingChange === change.id ? (
                        <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <><Check size={14} /> Approve</>
                      )}
                    </button>
                    <button
                      onClick={() => rejectChange(change.id)}
                      disabled={processingChange === change.id}
                      className="flex-1 h-8 bg-white dark:bg-white/10 border border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white text-xs font-bold flex items-center justify-center gap-1 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                      data-testid={`reject-change-${change.id}`}
                    >
                      <X size={14} /> Reject
                    </button>
                  </div>
                  )}
                  
                  <p className="text-[10px] text-[#30302F] dark:text-white mt-2 flex items-center gap-1">
                    <Clock size={10} /> Proposed {new Date(change.proposed_at).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
